// Stable, locale-aware formatters used across the UI. Pure functions.

export function fmtUsd(value: number | null | undefined, fractionDigits = 2): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "-";
  const sign = value < 0 ? "-" : "";
  const abs = Math.abs(value);
  return `${sign}$${abs.toLocaleString(undefined, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })}`;
}

export function fmtNum(value: number | null | undefined, fractionDigits = 2): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "-";
  return value.toLocaleString(undefined, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
}

export function fmtPct(value: number | null | undefined, fractionDigits = 2): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "-";
  return `${(value * 100).toFixed(fractionDigits)}%`;
}

export function fmtTime(iso: string | null | undefined): string {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

export function fmtRelative(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const diffSec = Math.floor((now.getTime() - d.getTime()) / 1000);
  if (diffSec < 5) return "just now";
  if (diffSec < 60) return `${diffSec}s ago`;
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  return `${Math.floor(diffSec / 86400)}d ago`;
}

export function fmtRelativeFuture(iso: string | null | undefined): string {
  if (!iso) return "";
  const ms = new Date(iso).getTime() - Date.now();
  if (!Number.isFinite(ms)) return "";
  if (ms < 0) return "any moment";
  const m = Math.round(ms / 60000);
  if (m < 1) return "<1 min";
  if (m < 60) return `in ${m} min`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem ? `in ${h}h ${rem}m` : `in ${h}h`;
}

/**
 * Smart price formatter for crypto tokens spanning many orders of magnitude.
 *   - >= 1000:   $12,345.67       (locale grouping, 2-4 decimals)
 *   - 1 to 1000: $42.5037         (4 decimals)
 *   - 0.01 to 1: $0.7563          (4-6 decimals)
 *   - < 0.01:    $0.00000358      (8 decimals if not too small) OR $3.58e-6 (very small)
 */
export function fmtPrice(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "-";
  if (value === 0) return "$0";
  const abs = Math.abs(value);
  const sign = value < 0 ? "-" : "";
  if (abs >= 1000) {
    return `${sign}$${abs.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
  }
  if (abs >= 1) {
    return `${sign}$${abs.toFixed(4)}`;
  }
  if (abs >= 0.01) {
    return `${sign}$${abs.toFixed(6)}`;
  }
  if (abs >= 0.000001) {
    // 6 decimals down to 10 — show all the trailing detail
    return `${sign}$${abs.toFixed(10)}`;
  }
  // Sub-microcent → scientific
  return `${sign}$${abs.toExponential(3)}`;
}

/**
 * Smart quantity formatter — uses K / M / B suffixes for readability.
 *   1,234,567 → "1.23M"
 *   12,345 → "12,345"
 *   0.0042 → "0.0042"
 */
export function fmtQty(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "-";
  if (value === 0) return "0";
  const abs = Math.abs(value);
  const sign = value < 0 ? "-" : "";
  if (abs >= 1_000_000_000) return `${sign}${(abs/1_000_000_000).toFixed(2)}B`;
  if (abs >= 1_000_000) return `${sign}${(abs/1_000_000).toFixed(2)}M`;
  if (abs >= 10_000) return `${sign}${abs.toLocaleString(undefined, {maximumFractionDigits: 0})}`;
  if (abs >= 1) return `${sign}${abs.toLocaleString(undefined, {maximumFractionDigits: 4})}`;
  if (abs >= 0.0001) return `${sign}${abs.toFixed(6)}`;
  return `${sign}${abs.toExponential(3)}`;
}

export function fmtDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || Number.isNaN(seconds)) return "-";
  const s = Math.max(0, Math.floor(seconds));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}
